import { z } from 'zod';

/**
 * Build/runtime environment contract for the widget.
 *
 * Only VITE_-prefixed vars are exposed to the bundle by Vite, so this schema
 * must never describe a secret. Used in two places:
 *   - vite.config.ts (build time) → fail the build fast on bad config.
 *   - config/env.ts (runtime) → typed access to the validated values.
 */
export const widgetEnvSchema = z.object({
  /** Base URL of OUR chat API (the PHP app). The browser only calls this. */
  VITE_API_BASE_URL: z.string().url(),
});

export type WidgetEnv = z.infer<typeof widgetEnvSchema>;

/**
 * Validate an environment source, throwing a readable error on failure.
 *
 * @param source `import.meta.env` at runtime, or `loadEnv(...)` at build time.
 */
export function parseWidgetEnv(source: Record<string, unknown>): WidgetEnv {
  const result = widgetEnvSchema.safeParse(source);

  if (!result.success) {
    const problems = result.error.issues
      .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('\n');

    throw new Error(`Widget environment validation failed:\n${problems}`);
  }

  return result.data;
}
